import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

export const SUPPORTED_LANGUAGES = ['de', 'en', 'es', 'zh-CN', 'fr', 'it'];
export const DEFAULT_LANGUAGE = 'en';
export const NAMESPACES = ['common', 'navigation', 'teamRequestForm', 'setupPage', 'adminPage', 'requestsPage', 'notifications', 'errors'];

// Calculate the correct base path for i18n resources
// Try multiple strategies to determine the correct base path
let BASE_PATH = '/';

// Strategy 1: Use import.meta.env.BASE_URL if available
if (import.meta.env.BASE_URL) {
  BASE_PATH = import.meta.env.BASE_URL;
  console.log('[i18n] Using import.meta.env.BASE_URL:', BASE_PATH);
} else {
  // Strategy 2: Detect from current location if in /tabs/home
  const currentPath = window.location.pathname;
  if (currentPath.includes('/tabs/home')) {
    BASE_PATH = '/tabs/home/';
    console.log('[i18n] Detected /tabs/home in pathname, using:', BASE_PATH);
  } else if (currentPath.includes('/tabs/')) {
    // Handle other tab paths
    const basePath = currentPath.split('/').slice(0, 3).join('/') + '/';
    BASE_PATH = basePath;
    console.log('[i18n] Detected tab path in pathname, using:', BASE_PATH);
  }
}

const LOCALES_PATH = `${BASE_PATH}locales/{{lng}}/{{ns}}.json`;

// 🔍 DEBUG: Log i18n initialization
console.log('[i18n] Initializing i18next with:', {
  supportedLanguages: SUPPORTED_LANGUAGES,
  defaultLanguage: DEFAULT_LANGUAGE,
  namespaces: NAMESPACES,
  basePath: BASE_PATH,
  localesPath: LOCALES_PATH,
  timestamp: new Date().toISOString(),
});

// Verify base path logic
console.log('[i18n] BASE_URL Debug Info:', {
  'import.meta.env.BASE_URL': import.meta.env.BASE_URL,
  'import.meta.env.VITE_TEAMS_APP_ID': import.meta.env.VITE_TEAMS_APP_ID,
  'window.location.pathname': typeof window !== 'undefined' ? window.location.pathname : 'N/A',
});

// Migration : supprimer toute valeur de langue auto-mise en cache par d'anciennes versions
// (quand caches: ['localStorage'] était actif). On ne conserve que les choix explicites.
const USER_CHOICE_KEY = 'requestateam_language_user_choice';
const cachedLang = localStorage.getItem('requestateam_language');
const isUserChoice = localStorage.getItem(USER_CHOICE_KEY) === 'true';
if (cachedLang && !isUserChoice) {
  console.log('[i18n] Migration: suppression de la langue auto-détectée en cache:', cachedLang);
  localStorage.removeItem('requestateam_language');
}

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    ns: NAMESPACES,
    defaultNS: 'common',
    backend: {
      loadPath: LOCALES_PATH,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'requestateam_language',
      // Ne pas mettre en cache la langue détectée automatiquement depuis le navigateur.
      // Seule une sélection explicite via LanguageSelector doit persister dans localStorage.
      // Cela permet à useTeamsLocale d'appliquer la locale Teams (fr-FR, etc.) correctement.
      caches: [],
    },
    interpolation: { escapeValue: false },
    react: { useSuspense: true },
  });

// 🔍 DEBUG: Log language change events
i18n.on('languageChanged', (lng) => {
  console.log('[i18n] Language changed to:', lng, {
    timestamp: new Date().toISOString(),
    localStorage: localStorage.getItem('requestateam_language'),
    i18nLanguage: i18n.language,
  });
});

i18n.on('loaded', (loaded) => {
  console.log('[i18n] Resources loaded:', {
    loaded,
    timestamp: new Date().toISOString(),
    language: i18n.language,
    readyState: i18n.isInitialized ? 'initialized' : 'not initialized',
  });
});

i18n.on('failedLoading', (lng, ns, msg) => {
  console.error('[i18n] Failed to load resources:', { lng, ns, msg, timestamp: new Date().toISOString() });
});

i18n.on('missingKey', (lngs, ns, key) => {
  console.warn('[i18n] Missing translation key:', { lngs, ns, key });
});

// Monitor resource loading via backend events
if (i18n.services.backendConnector?.on) {
  i18n.services.backendConnector.on('backendLoaded', (data) => {
    console.log('[i18n] Backend resources loaded:', { data });
  });
}

export default i18n;
